import { defineStore } from "pinia"
import { i18n } from "@/i18n/index"
import { AbilitiesApi } from "@/services/abilities-service"
import { useModal } from "@/stores/modal"
import { useAuth } from "@/stores/auth"
import { useSystemFeedback } from "@/stores/systemFeedback"
import type {
  Abilities,
  AbilitiesFilters,
  FavoriteAbility
} from "@/types/abilities"
import type { Pagination } from "@/types/pagination"
import type { ApiError } from "@/types/api"

const { t } = i18n.global

export const useAbilities = defineStore("abilities", {
  state: () => {
    return {
      data: {} as Pagination<Abilities>,
      dataFilter: {} as AbilitiesFilters,
      favoriteData: {} as Pagination<FavoriteAbility<Abilities>>,
      ability: {} as Abilities,
      api: new AbilitiesApi(),
      isLoading: false
    }
  },

  actions: {
    async getAbilities(page: number, search?: string, order?: string) {
      this.isLoading = true
      try {
        const favoritesResponse = await this.api.getFavoriteAbility(
          1,
          "",
          "",
          99999
        )
        this.api.setSearch(search)
        this.api.setOrder(order)
        const response = await this.api.getAbilities(page, search, order)
        if (response.data) {
          this.data = response.data
          this.favoriteData = favoritesResponse.data
        }
        this.isLoading = false
        return response
      } catch (error) {
        this.handleErrorsOnModal(error as ApiError)
        this.isLoading = false
      }
    },

    async getAbilitiesFilter(query?: string) {
      try {
        const response = await this.api.getAbilitiesFilters(query)
        if (response.data) {
          this.dataFilter = response.data
        }
        return response
      } catch (error) {
        this.handleErrorsOnModal(error as ApiError)
      }
    },

    async getAbility(id: string) {
      this.ability = {} as Abilities
      try {
        const response = await this.api.getAbility(id)
        if (response.data) {
          this.ability = response.data
        }
        return response
      } catch (error) {
        this.handleErrorsOnModal(error as ApiError)
      }
    },

    async postFavoriteAbility(data: Abilities) {
      try {
        const auth = useAuth()
        const loggedUserId = auth.loggedUser.id
        const response = await this.api.postFavoriteAbility({
          id_user: loggedUserId,
          menu: "abilities",
          deprecated: false,
          favorite_objects: data
        })
        return response
      } catch (error) {
        this.handleErrorsOnModal(error as ApiError)
      }
    },

    async getFavoriteAbility(
      page: number,
      search?: string,
      order?: string,
      perPage = 12
    ) {
      this.isLoading = true
      try {
        this.api.setSearch(search)
        this.api.setOrder(order)
        const response = await this.api.getFavoriteAbility(
          page,
          search,
          order,
          perPage
        )
        if (response.data) {
          const parsedFavoriteData = {
            ...response.data,
            content: response.data.content.map(e => e.favorite_objects)
          }
          this.data = parsedFavoriteData
        }
        this.isLoading = false
        return response
      } catch (error) {
        this.handleErrorsOnModal(error as ApiError)
        this.isLoading = false
      }
    },

    async getTactics() {
      try {
        const response = await this.api.getTactics()
        return response
      } catch (error) {
        this.handleErrorsOnModal(error as ApiError)
      }
    },

    async getParserResults() {
      try {
        const response = await this.api.getParserResults()
        return response
      } catch (error) {
        this.handleErrorsOnModal(error as ApiError)
      }
    },

    async postAbility(data: FormData) {
      try {
        const response = await this.api.postAbility(data)
        this.handleSuccessMessage(response.data.detail)
        return response
      } catch (error) {
        this.handleErrorsOnAlert(error as ApiError)
      }
    },

    async updateAbility(id: string, data: FormData) {
      try {
        const response = await this.api.updateAbility(id, data)
        this.handleSuccessMessage(response.data.detail)
        return response
      } catch (error) {
        this.handleErrorsOnAlert(error as ApiError)
      }
    },

    async deletePayload(payloadName: string, abilityId: string) {
      try {
        const response = await this.api.deletePayload(payloadName, abilityId)
        this.handleSuccessMessage(response.data.detail)
        return response
      } catch (error) {
        this.handleErrorsOnAlert(error as ApiError)
      }
    },

    getErrorMessage(error: ApiError) {
      return error?.response
        ? `backend.${error.response.data.detail}`
        : "requestError.default"
    },

    handleErrorsOnModal(error: ApiError) {
      const errorMessage = this.getErrorMessage(error)
      if (errorMessage) {
        useModal().triggerModal({
          icon: {
            name: "ExclamationIcon"
          },
          button: {
            confirmation: {
              styles: "w-full text-white border-[#FF005C]",
              text: t("general.ok"),
              click: () => useModal().closeModal()
            }
          },
          hasCloseIcon: true,
          title: t("requestError.title"),
          text: t(errorMessage),
          hasButtons: true
        })
      }
    },

    handleErrorsOnAlert(error: ApiError) {
      const errorMessage = this.getErrorMessage(error)
      useSystemFeedback().setFeedback({ type: "danger", text: errorMessage })
    },

    handleSuccessMessage(message: string) {
      useSystemFeedback().setFeedback({
        type: "success",
        text: `backend.${message}`
      })
    }
  }
})
